// Denunciar e bloquear jogadores (chat, fim da partida, lista de amigos).
// A lista de bloqueados fica em memória para esconder mensagens na hora.
import { api, errorMessage } from "./api";
import { t, type MsgKey } from "./i18n";
import { esc } from "./ui";

export const REPORT_REASONS = ["nickname", "chat", "afk", "cheating", "other"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

let blocked = new Set<string>();
let loaded = false;

export async function loadBlocks(): Promise<Set<string>> {
  if (loaded) return blocked;
  try {
    blocked = new Set((await api.blocks()).blocked.map((b) => b.id));
    loaded = true;
  } catch {
    /* sem sessão: nada bloqueado */
  }
  return blocked;
}

export function isBlocked(userId: string | null | undefined): boolean {
  return !!userId && blocked.has(userId);
}

export function resetBlocks(): void {
  blocked = new Set();
  loaded = false;
}

export interface ReportTarget {
  id: string;
  nickname: string;
  matchId?: string;
  /** mensagens da sala da partida (o servidor não guarda o chat da sala) */
  roomChat?: { nickname: string; text: string; at: number }[];
}

/** Abre a janela de denúncia/bloqueio. Resolve quando fecha. */
export function openReport(target: ReportTarget): Promise<void> {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "preview-modal report-modal";
    modal.innerHTML = `
      <form class="bw-panel bw-stack report-form" role="dialog" aria-label="${esc(t("report.title", { name: target.nickname }))}">
        <div class="bw-panel__header">${esc(t("report.title", { name: target.nickname }))}</div>
        <fieldset class="report-reasons">
          <legend class="bw-dim small">${t("report.why")}</legend>
          ${REPORT_REASONS.map((r, i) => `<label class="report-reason"><input type="radio" name="reason" value="${r}" ${i === 0 ? "checked" : ""}> ${t(`report.reason.${r}` as MsgKey)}</label>`).join("")}
        </fieldset>
        <textarea class="bw-input" name="details" maxlength="500" rows="3" placeholder="${t("report.details")}"></textarea>
        <label class="report-reason"><input type="checkbox" name="block" ${isBlocked(target.id) ? "checked disabled" : ""}> ${t("report.alsoBlock")}</label>
        <p class="bw-dim small">${t("report.note")}</p>
        <div data-alert hidden></div>
        <div class="report-actions">
          <button type="button" class="bw-btn" data-cancel>${t("common.close")}</button>
          <button type="button" class="bw-btn" data-block>${isBlocked(target.id) ? t("report.unblock") : t("report.blockOnly")}</button>
          <button type="submit" class="bw-btn bw-btn--red">${t("report.send")}</button>
        </div>
      </form>`;
    const close = () => {
      modal.remove();
      resolve();
    };
    const alert = (msg: string, kind = "error") => {
      const a = modal.querySelector<HTMLElement>("[data-alert]")!;
      a.className = `bw-alert bw-alert--${kind}`;
      a.textContent = msg;
      a.hidden = false;
    };
    modal.addEventListener("click", async (e) => {
      const el = e.target as HTMLElement;
      if (el === modal || el.closest("[data-cancel]")) return close();
      if (el.closest("[data-block]")) {
        try {
          if (isBlocked(target.id)) {
            await api.unblock(target.id);
            blocked.delete(target.id);
          } else {
            await api.block(target.id);
            blocked.add(target.id);
          }
          window.dispatchEvent(new CustomEvent("pokechess:blocks"));
          close();
        } catch (err) {
          alert(errorMessage(err));
        }
      }
    });
    modal.querySelector("form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget as HTMLFormElement);
      const btn = modal.querySelector<HTMLButtonElement>("button[type=submit]")!;
      btn.setAttribute("aria-busy", "true");
      try {
        await api.report({
          targetId: target.id, reason: String(fd.get("reason")) as ReportReason, details: String(fd.get("details") ?? "").trim() || undefined,
          matchId: target.matchId, roomChat: target.roomChat?.slice(-30),
        });
        if (fd.get("block") && !isBlocked(target.id)) {
          await api.block(target.id);
          blocked.add(target.id);
          window.dispatchEvent(new CustomEvent("pokechess:blocks"));
        }
        alert(t("report.thanks"), "ok");
        setTimeout(close, 1400);
      } catch (err) {
        alert(errorMessage(err));
      } finally {
        btn.removeAttribute("aria-busy");
      }
    });
    document.body.appendChild(modal);
    modal.querySelector<HTMLInputElement>("input[name=reason]")?.focus();
  });
}

/** Botão pequeno "⚑" para colocar ao lado de um nome. */
export function reportButton(userId: string, nickname: string): string {
  return `<button type="button" class="bw-link report-btn" data-report="${esc(userId)}" data-report-name="${esc(nickname)}" title="${esc(t("report.button"))}" aria-label="${esc(t("report.title", { name: nickname }))}">⚑</button>`;
}
